/**
 * PurchaseHistoryTable — displays payment history as a shadcn Table.
 *
 * Columns: Data, Kwota, Status, Faktura
 * Empty state: "Brak płatności"
 * Status colors:
 *   - succeeded → text-primary (gold)
 *   - failed → text-destructive (red)
 *   - pending → text-muted-foreground (grey)
 */

import { useTranslation } from "react-i18next"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { PaymentOut } from "@/services/api"

interface PurchaseHistoryTableProps {
  payments: PaymentOut[]
}

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString("pl-PL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  } catch {
    return iso
  }
}

const formatAmount = (cents: number, currency: string): string => {
  return `${(cents / 100).toFixed(2).replace(".", ",")} ${currency}`
}

const STATUS_KEY_MAP: Record<string, string> = {
  succeeded: "billing.statusSucceeded",
  failed: "billing.statusFailed",
  pending: "billing.statusPending",
}

const statusClass: Record<string, string> = {
  succeeded: "text-primary",
  failed: "text-destructive",
  pending: "text-muted-foreground",
}

const truncateInvoiceId = (id: string): string => {
  if (id.length <= 14) return id
  return `${id.slice(0, 8)}…${id.slice(-3)}`
}

/** One-off purchases carry a payment intent instead of an invoice. */
const paymentReference = (payment: PaymentOut): string =>
  payment.stripe_invoice_id ?? payment.stripe_payment_intent_id ?? ""

export function PurchaseHistoryTable({ payments }: PurchaseHistoryTableProps) {
  const { t } = useTranslation()

  if (payments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <span className="text-4xl">🧾</span>
        <p className="text-muted-foreground text-sm">{t("billing.noPayments")}</p>
      </div>
    )
  }

  // Backend returns payments sorted by created_at DESC (newest first)
  return (
    <div className="overflow-x-auto">
      <Table aria-label={t("billing.historyAriaLabel")}>
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="text-muted-foreground">{t("billing.date")}</TableHead>
            <TableHead className="text-muted-foreground">{t("billing.amount")}</TableHead>
            <TableHead className="text-muted-foreground">{t("billing.status")}</TableHead>
            <TableHead className="text-muted-foreground hidden sm:table-cell">{t("billing.invoice")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((payment) => (
            <TableRow key={payment.id} className="border-border">
              <TableCell className="text-foreground">
                {formatDate(payment.created_at)}
              </TableCell>
              <TableCell className="text-foreground">
                {formatAmount(payment.amount_cents, payment.currency)}
              </TableCell>
              <TableCell
                className={statusClass[payment.status] ?? "text-muted-foreground"}
              >
                {STATUS_KEY_MAP[payment.status] != null
                  ? t(STATUS_KEY_MAP[payment.status])
                  : payment.status}
              </TableCell>
              <TableCell
                className="text-muted-foreground font-mono text-xs hidden sm:table-cell"
                title={paymentReference(payment)}
              >
                {truncateInvoiceId(paymentReference(payment))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
